import React, { useState, useEffect } from 'react';
import TipsModal from './components/TipsModal';
import UserSettings from './components/UserSettings';
import { initializeApp } from 'firebase/app';
import { getAuth, createUserWithEmailAndPassword, signInWithEmailAndPassword, signOut, onAuthStateChanged, sendPasswordResetEmail } from 'firebase/auth';
import { getFirestore, doc, setDoc, getDoc, getDocs, collection, updateDoc, deleteDoc } from 'firebase/firestore';
import { getStorage } from 'firebase/storage';

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID
};

const app = initializeApp(firebaseConfig);
const auth = getAuth(app);
const db = getFirestore(app);
const storage = getStorage(app);

function App() {
  const [currentUser, setCurrentUser] = useState(null);
  const [dogs, setDogs] = useState([]);
  const [selectedDog, setSelectedDog] = useState(null);
  const [entries, setEntries] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [weeklySummaries, setWeeklySummaries] = useState([]);
  const [allClients, setAllClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showDogForm, setShowDogForm] = useState(false);
  const [showEntryForm, setShowEntryForm] = useState(false);
  const [showQuestionForm, setShowQuestionForm] = useState(false);
  const [showWeeklySummary, setShowWeeklySummary] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [activeTab, setActiveTab] = useState('log');
  const [toast, setToast] = useState(null);
  const [showSettings, setShowSettings] = useState(false);
  // Note: showTipsModal already exists
  const [editingDog, setEditingDog] = useState(null);
  const [expandedClients, setExpandedClients] = useState({});
  const [showAnswered, setShowAnswered] = useState(false);
  const [expandedSummaries, setExpandedSummaries] = useState({});
  const [trainerSelectedClient, setTrainerSelectedClient] = useState(null);
  const [trainerActiveTab, setTrainerActiveTab] = useState('overview');
  
  const [loginData, setLoginData] = useState({ 
    email: '', password: '', isSignup: false, name: '', accountType: 'client', agreeToTerms: true 
  });
  
  const [dogFormData, setDogFormData] = useState({
    name: '', breed: '', age: '', spayedNeutered: 'unknown', photo: null
  });

  const [entryData, setEntryData] = useState({
    date: new Date().toISOString().split('T')[0],
    time: new Date().toTimeString().slice(0, 5),
    activity: '',
    customActivity: '',
    duration: '',
    issues: '',
    observations: '',
    dogMood: 'neutral',
    success: 'moderate',
    photos: [],
    videoLink: ''
  });

  const [questionData, setQuestionData] = useState({
    question: '',
    dogId: ''
  });

  const [weeklyData, setWeeklyData] = useState({
    summary: '',
    weekStart: new Date().toISOString().split('T')[0]
  });

  const activityOptions = [
    "Basic obedience (sit, stay, come)",
    "Leash walking/loose leash",
    "Crate training",
    "Socialization with people",
    "Socialization with dogs",
    "Trick training",
    "Impulse control exercises",
    "Recall practice",
    "Place/mat training",
    "Desensitization work",
    "Other"
  ];
  
  const [showTipsModal, setShowTipsModal] = useState(false);

  const showToast = (message, type = 'success') => {
  setToast({ message, type });
  setTimeout(() => setToast(null), 4000); // Auto-dismiss after 4 seconds
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        if (userDoc.exists()) {
          const userData = { ...userDoc.data(), uid: user.uid };
          setCurrentUser(userData);
         // Show random tip once per day
  const lastShown = localStorage.getItem('lastTipShown');
  const today = new Date().toDateString();
  if (lastShown !== today && userData.accountType !== 'trainer') {
  setTimeout(() => setShowTipsModal(true), 1000); // Delay 1 second for better UX
  localStorage.setItem('lastTipShown', today);
        }
          if (userData.accountType === 'trainer') {
            loadAllClients();
          } else {
            loadUserDogs(user.uid);
            loadQuestions(user.uid);
            loadWeeklySummaries(user.uid);
          }
        }
      }
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  const loadAllClients = async () => {
    try {
      console.log('🔍 Loading all clients...');
      const usersSnapshot = await getDocs(collection(db, 'users'));
      console.log('📊 Total users in database:', usersSnapshot.docs.length);
      
      const clientData = [];
      for (const userDoc of usersSnapshot.docs) {
        const user = userDoc.data();
        console.log(`👤 Checking user ${userDoc.id}:`, {
          accountType: user.accountType,
          name: user.name,
          email: user.email
        });
        
        // Only skip trainers - show all client accounts
        if (user.accountType !== 'trainer') {
          const dogsSnapshot = await getDocs(collection(db, `users/${userDoc.id}/dogs`));
          const dogs = dogsSnapshot.docs.map(d => ({ id: d.id, ...d.data() }));
          
          const questionsSnapshot = await getDocs(collection(db, `users/${userDoc.id}/questions`));
          const questions = questionsSnapshot.docs.map(q => ({ id: q.id, ...q.data() }));
          
          const summariesSnapshot = await getDocs(collection(db, `users/${userDoc.id}/summaries`));
          const summaries = summariesSnapshot.docs.map(s => ({ id: s.id, ...s.data() }));
          
          // Load ALL training entries for this client (for all their dogs)
          const allEntries = [];
          for (const dog of dogs) {
            const entriesSnapshot = await getDocs(collection(db, `users/${userDoc.id}/dogs/${dog.id}/entries`));
            const dogEntries = entriesSnapshot.docs.map(e => ({ id: e.id, dogId: dog.id, dogName: dog.name, ...e.data() }));
            allEntries.push(...dogEntries);
          }
          
          // Use email as display name if name is missing
          const displayName = user.name || user.email || 'Unknown Client';
          clientData.push({ ...user, name: displayName, uid: userDoc.id, dogs, questions, summaries, entries: allEntries });
          console.log(`✅ Added client: ${displayName}`);
        } else {
          console.log(`⏭️ Skipped trainer: ${user.name || user.email}`);
        }
      }
      setAllClients(clientData);
      console.log('✅ Loaded clients:', clientData.length);
      console.log('📋 Client list:', clientData.map(c => c.name));
      return clientData; // Return the loaded data so it can be used immediately
    } catch (error) {
      console.error('❌ Error loading clients:', error);
      return []; // Return empty array on error
    }
  };

  const loadUserDogs = async (userId) => {
    try {
      const dogsSnapshot = await getDocs(collection(db, `users/${userId}/dogs`));
      const userDogs = dogsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setDogs(userDogs);
      if (userDogs.length > 0) {
        setSelectedDog(userDogs[0]);
        loadDogEntries(userId, userDogs[0].id);
      }
    } catch (error) {
      console.error('Error loading dogs:', error);
    }
  };

  const loadDogEntries = async (userId, dogId) => {
    try {
      const entriesSnapshot = await getDocs(collection(db, `users/${userId}/dogs/${dogId}/entries`));
      const dogEntries = entriesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      dogEntries.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
      setEntries(dogEntries);
    } catch (error) {
      console.error('Error loading entries:', error);
      setEntries([]);
    }
  };

  const loadQuestions = async (userId) => {
    try {
      const questionsSnapshot = await getDocs(collection(db, `users/${userId}/questions`));
      const userQuestions = questionsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      userQuestions.sort((a, b) => new Date(b.askedAt) - new Date(a.askedAt));
      setQuestions(userQuestions);
    } catch (error) {
      console.error('Error loading questions:', error);
      setQuestions([]);
    }
  };

  const loadWeeklySummaries = async (userId) => {
    try {
      const summariesSnapshot = await getDocs(collection(db, `users/${userId}/summaries`));
      const userSummaries = summariesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      userSummaries.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
      setWeeklySummaries(userSummaries);
    } catch (error) {
      console.error('Error loading summaries:', error);
      setWeeklySummaries([]);
    }
  };

  const handleLogin = async () => {
    if (!loginData.email || !loginData.password) {
      showToast('Please enter email and password', 'error');
      return;
    }
    try {
      if (loginData.isSignup) {
        if (!loginData.name) {
          showToast('Please enter your name', 'error');
          return;
        }
        if (!loginData.agreeToTerms) {
          showToast('Please agree to the Terms & Privacy Policy to create an account', 'error');
          return;
        }
        
        // Execute reCAPTCHA v3
        if (window.grecaptcha) {
          try {
            const token = await window.grecaptcha.execute(process.env.REACT_APP_RECAPTCHA_SITE_KEY, {action: 'signup'});
            console.log('reCAPTCHA token generated:', token ? 'Success' : 'Failed');
            // Token generated successfully - proceed with signup
            // Note: For full security, this token should be verified on a backend server
          } catch (error) {
            console.error('reCAPTCHA error:', error);
            showToast('Security verification failed. Please try again.', 'error');
            return;
          }
        } else {
          console.warn('reCAPTCHA not loaded');
        }
        
        const userCredential = await createUserWithEmailAndPassword(auth, loginData.email, loginData.password);
        await setDoc(doc(db, 'users', userCredential.user.uid), {
          email: loginData.email,
          name: loginData.name,
          accountType: 'client', // Always client for signups
          createdAt: new Date().toISOString()
        });
        showToast('Account created successfully!', 'success');
      } else {
        // Login existing user
        const userCredential = await signInWithEmailAndPassword(auth, loginData.email, loginData.password);
        
        // Check if user document exists in Firestore
        const userDoc = await getDoc(doc(db, 'users', userCredential.user.uid));
        
        if (!userDoc.exists()) {
          // User authenticated but no Firestore doc - create one
          await setDoc(doc(db, 'users', userCredential.user.uid), {
            email: loginData.email,
            name: loginData.email.split('@')[0],
            accountType: 'client',
            createdAt: new Date().toISOString()
          });
        }
        
        showToast('Login successful!', 'success');
      }
      setLoginData({ email: '', password: '', isSignup: false, name: '', accountType: 'client', agreeToTerms: true });
    } catch (error) {
      console.error('Login error:', error);
      
      // Better error messages
      let errorMessage = 'Login failed. Please try again.';
      
      if (error.code === 'auth/wrong-password' || error.code === 'auth/invalid-credential') {
        errorMessage = 'Incorrect password. Please try again.';
      } else if (error.code === 'auth/user-not-found') {
        errorMessage = 'No account found with this email.';
      } else if (error.code === 'auth/invalid-email') {
        errorMessage = 'Please enter a valid email address.';
      } else if (error.code === 'auth/too-many-requests') {
        errorMessage = 'Too many failed attempts. Try again later or reset your password.';
      } else if (error.code === 'auth/email-already-in-use') {
        errorMessage = 'This email is already registered. Try logging in instead.';
      } else if (error.code === 'auth/weak-password') {
        errorMessage = 'Password should be at least 6 characters.';
      } else if (error.code === 'auth/network-request-failed') {
        errorMessage = 'Network error. Check your internet connection.';
      }
      
      showToast(errorMessage, 'error');
    }
  };

  const handleForgotPassword = async () => {
    if (!resetEmail) {
      showToast('Please enter your email address');
      return;
    }
    try {
      await sendPasswordResetEmail(auth, resetEmail);
      showToast('Password reset email sent! Check your inbox (and spam folder).');
      setShowForgotPassword(false);
      setResetEmail('');
    } catch (error) {
      showToast('Error: ' + error.message);
    }
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
      setCurrentUser(null);  // ← This was missing!
      setDogs([]);
      setSelectedDog(null);
      setEntries([]);
      setQuestions([]);
      setWeeklySummaries([]);
      setAllClients([]);
    } catch (error) {
      console.error('Logout error:', error);
      showToast('Error logging out. Please try again.');
    }
  };

  const handlePhotoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          const MAX_WIDTH = 800;
          const MAX_HEIGHT = 800;
          let width = img.width;
          let height = img.height;
          
          if (width > height) {
            if (width > MAX_WIDTH) {
              height *= MAX_WIDTH / width;
              width = MAX_WIDTH;
            }
          } else {
            if (height > MAX_HEIGHT) {
              width *= MAX_HEIGHT / height;
              height = MAX_HEIGHT;
            }
          }
          
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);
          
          const compressedImage = canvas.toDataURL('image/jpeg', 0.7);
          setDogFormData({...dogFormData, photo: compressedImage});
        };
        img.src = reader.result;
      };
      reader.readAsDataURL(file);
    }
  };

  const handleEntryPhotoUpload = (e) => {
    const files = Array.from(e.target.files);
    files.forEach(file => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          const MAX_WIDTH = 800;
          const MAX_HEIGHT = 800;
          let width = img.width;
          let height = img.height;
          
          if (width > height) {
            if (width > MAX_WIDTH) {
              height *= MAX_WIDTH / width;
              width = MAX_WIDTH;
            }
          } else {
            if (height > MAX_HEIGHT) {
              width *= MAX_HEIGHT / height;
              height = MAX_HEIGHT;
            }
          }
          
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);
          
          const compressedImage = canvas.toDataURL('image/jpeg', 0.7);
          setEntryData({...entryData, photos: [...entryData.photos, compressedImage]});
        };
        img.src = reader.result;
      };
      reader.readAsDataURL(file);
    });
  };

  const removePhoto = (index) => {
    const newPhotos = entryData.photos.filter((_, i) => i !== index);
    setEntryData({...entryData, photos: newPhotos});
  };

  const handleAddDog = async () => {
    if (!dogFormData.name || !dogFormData.breed || !dogFormData.age) {
      showToast('Please fill in all dog information');
      return;
    }
    try {
      const dogRef = doc(collection(db, `users/${currentUser.uid}/dogs`));
      await setDoc(dogRef, {
        ...dogFormData,
        createdAt: new Date().toISOString()
      });
      await loadUserDogs(currentUser.uid);
      setDogFormData({ name: '', breed: '', age: '', spayedNeutered: 'unknown', photo: null });
      setShowDogForm(false);
    } catch (error) {
      showToast('Error saving dog: ' + error.message);
    }
  };
  const handleEditDog = async () => {
  if (!dogFormData.name || !dogFormData.breed || !dogFormData.age) {
    showToast('Please fill in all dog information', 'error');
    return;
  }

  try {
    await updateDoc(doc(db, `users/${currentUser.uid}/dogs`, editingDog.id), {
      name: dogFormData.name,
      breed: dogFormData.breed,
      age: dogFormData.age,
      spayedNeutered: dogFormData.spayedNeutered,
      photo: dogFormData.photo
    });

    await loadUserDogs(currentUser.uid);
    setDogFormData({ name: '', breed: '', age: '', spayedNeutered: 'unknown', photo: null });
    setEditingDog(null);
    setShowDogForm(false);
    showToast('Dog profile updated!', 'success');
  } catch (error) {
    console.error('Error updating dog:', error);
    showToast('Error updating dog: ' + error.message, 'error');
  }
  };

  const handleDeleteDog = async (dogId) => {
  if (!window.confirm('Are you sure you want to delete this dog profile? This will also delete all training entries.')) {
    return;
  }

  try {
    // Delete all entries for this dog first
    const entriesSnapshot = await getDocs(collection(db, `users/${currentUser.uid}/dogs/${dogId}/entries`));
    const deletePromises = entriesSnapshot.docs.map(doc => deleteDoc(doc.ref));
    await Promise.all(deletePromises);

    // Delete the dog
    await deleteDoc(doc(db, `users/${currentUser.uid}/dogs`, dogId));

    await loadUserDogs(currentUser.uid);
    if (selectedDog?.id === dogId) {
      setSelectedDog(null);
      setEntries([]);
    }
    showToast('Dog profile deleted', 'success');
  } catch (error) {
    console.error('Error deleting dog:', error);
    showToast('Error deleting dog: ' + error.message, 'error');
  }
  };

  const handleSaveEntry = async () => {
    const activityValue = entryData.activity === 'Other' && entryData.customActivity 
      ? entryData.customActivity : entryData.activity;
      
    if (!activityValue || !entryData.duration || !entryData.observations) {
      showToast('Please fill in all required fields');
      return;
    }
    
    try {
      if (editingEntry) {
        await updateDoc(doc(db, `users/${currentUser.uid}/dogs/${selectedDog.id}/entries`, editingEntry.id), {
          ...entryData,
          activity: activityValue
        });
        setEditingEntry(null);
      } else {
        await setDoc(doc(collection(db, `users/${currentUser.uid}/dogs/${selectedDog.id}/entries`)), {
          ...entryData,
          activity: activityValue,
          timestamp: new Date().toISOString()
        });
      }
      
      await loadDogEntries(currentUser.uid, selectedDog.id);
      setEntryData({
        date: new Date().toISOString().split('T')[0],
        time: new Date().toTimeString().slice(0, 5),
        activity: '', customActivity: '', duration: '', issues: '', observations: '',
        dogMood: 'neutral', success: 'moderate', photos: [], videoLink: ''
      });
      setShowEntryForm(false);
    } catch (error) {
      showToast('Error saving entry: ' + error.message);
    }
  };

  const handleEditEntry = (entry) => {
    setEntryData({
      date: entry.date,
      time: entry.time,
      activity: entry.activity,
      customActivity: '',
      duration: entry.duration,
      issues: entry.issues || '',
      observations: entry.observations || '',
      dogMood: entry.dogMood || 'neutral',
      success: entry.success || 'moderate',
      photos: entry.photos || [],
      videoLink: entry.videoLink || ''
    });
    setEditingEntry(entry);
    setShowEntryForm(true);
    setActiveTab('log');
  };

  const handleDeleteEntry = async (entryId) => {
    if (window.confirm('Delete this entry?')) {
      try {
        await deleteDoc(doc(db, `users/${currentUser.uid}/dogs/${selectedDog.id}/entries`, entryId));
        await loadDogEntries(currentUser.uid, selectedDog.id);
      } catch (error) {
        showToast('Error: ' + error.message);
      }
    }
  };

  const handleSubmitQuestion = async () => {
    if (!questionData.question) {
      showToast('Please enter a question');
      return;
    }

    try {
      const dogForQuestion = questionData.dogId 
        ? dogs.find(d => d.id === questionData.dogId)
        : selectedDog;

      await setDoc(doc(collection(db, `users/${currentUser.uid}/questions`)), {
        question: questionData.question,
        dogId: dogForQuestion?.id,
        dogName: dogForQuestion?.name,
        askedAt: new Date().toISOString(),
        answer: null,
        answeredAt: null
      });

      await loadQuestions(currentUser.uid);
      setQuestionData({ question: '', dogId: '' });
      setShowQuestionForm(false);
      showToast('Question sent to your trainer!');
    } catch (error) {
      showToast('Error sending question: ' + error.message);
    }
  };

  const handleSubmitWeeklySummary = async () => {
    if (!weeklyData.summary) {
      showToast('Please enter a summary');
      return;
    }

    try {
      await setDoc(doc(collection(db, `users/${currentUser.uid}/summaries`)), {
        summary: weeklyData.summary,
        weekStart: weeklyData.weekStart,
        createdAt: new Date().toISOString()
      });

      await loadWeeklySummaries(currentUser.uid);
      setWeeklyData({ summary: '', weekStart: new Date().toISOString().split('T')[0] });
      setShowWeeklySummary(false);
      showToast('Weekly summary saved!');
    } catch (error) {
      showToast('Error saving summary: ' + error.message);
    }
  };

  const handleAnswerQuestion = async (clientUid, questionId, answer) => {
    try {
      await updateDoc(doc(db, `users/${clientUid}/questions`, questionId), {
        answer,
        answeredAt: new Date().toISOString(),
        answeredBy: currentUser.name
      });
      
      showToast('Answer sent!', 'success');
      
      // Reload all clients to get the updated question data
      const refreshedClients = await loadAllClients();
      
      // Process the refreshed client to add computed properties
      // (same processing as done in the trainer dashboard render)
      const processedClient = refreshedClients
        .map(client => ({
          ...client,
          unansweredCount: (client.questions || []).filter(q => !q.answer).length,
          answeredCount: (client.questions || []).filter(q => q.answer).length,
          unansweredQuestions: (client.questions || []).filter(q => !q.answer).sort((a, b) => new Date(b.askedAt) - new Date(a.askedAt)),
          answeredQuestions: (client.questions || []).filter(q => q.answer).sort((a, b) => new Date(b.askedAt) - new Date(a.askedAt))
        }))
        .find(c => c.uid === clientUid);
      
      if (processedClient) {
        setTrainerSelectedClient(processedClient);
      }
      
    } catch (error) {
      console.error('Error saving answer:', error);
      showToast('Error saving answer: ' + error.message, 'error');
    }
  };

  const handleRemoveClient = async (clientUid) => {
    if (!window.confirm('Are you sure you want to remove this client from your view? This will not delete their account, just hide them from your dashboard.')) {
      return;
    }
    
    try {
      // Add a "hidden" flag to the client's user document
      await updateDoc(doc(db, 'users', clientUid), {
        hiddenFromTrainer: true
      });
      
      showToast('Client removed from your view', 'success');
      
      // Reload clients and clear selection
      await loadAllClients();
      setTrainerSelectedClient(null);
    } catch (error) {
      console.error('Error removing client:', error);
      showToast('Error removing client: ' + error.message, 'error');
    }
  };

  const handleDeleteAccount = async () => {
    const confirmMessage = currentUser.accountType === 'trainer'
      ? 'Are you sure you want to delete your trainer account? This will permanently delete all client data you can see. This action cannot be undone.'
      : 'Are you sure you want to delete your account? This will permanently delete all your dogs, training entries, questions, and summaries. This action cannot be undone.';
    
    if (!window.confirm(confirmMessage)) {
      return;
    }
    
    const doubleConfirm = window.prompt('Type DELETE to confirm account deletion:');
    if (doubleConfirm !== 'DELETE') {
      showToast('Account deletion cancelled', 'error');
      return;
    }
    
    try {
      const userId = currentUser.uid;
      
      // Delete all subcollections (dogs, questions, summaries)
      if (currentUser.accountType === 'client') {
        // Delete all dog entries first
        const dogsSnapshot = await getDocs(collection(db, `users/${userId}/dogs`));
        for (const dogDoc of dogsSnapshot.docs) {
          const entriesSnapshot = await getDocs(collection(db, `users/${userId}/dogs/${dogDoc.id}/entries`));
          const deleteEntriesPromises = entriesSnapshot.docs.map(doc => deleteDoc(doc.ref));
          await Promise.all(deleteEntriesPromises);
          await deleteDoc(dogDoc.ref);
        }
        
        // Delete questions
        const questionsSnapshot = await getDocs(collection(db, `users/${userId}/questions`));
        const deleteQuestionsPromises = questionsSnapshot.docs.map(doc => deleteDoc(doc.ref));
        await Promise.all(deleteQuestionsPromises);
        
        // Delete summaries
        const summariesSnapshot = await getDocs(collection(db, `users/${userId}/summaries`));
        const deleteSummariesPromises = summariesSnapshot.docs.map(doc => deleteDoc(doc.ref));
        await Promise.all(deleteSummariesPromises);
      }
      
      // Delete user document
      await deleteDoc(doc(db, 'users', userId));
      
      // Delete Firebase Auth account
      await auth.currentUser.delete();
      
      showToast('Account deleted successfully', 'success');
      
      // Reset state
      setCurrentUser(null);
      setDogs([]);
      setSelectedDog(null);
      setEntries([]);
      setQuestions([]);
      setWeeklySummaries([]);
      setAllClients([]);
    } catch (error) {
      console.error('Error deleting account:', error);
      showToast('Error deleting account: ' + error.message, 'error');
    }
  };

  const handleAddEntry = async () => {
  // Validation
  if (!entryData.activity) {
    showToast('Please select an activity', 'error');
    return;
  }
  
  if (entryData.activity === 'Other' && !entryData.customActivity.trim()) {
    showToast('Please specify the custom activity', 'error');
    return;
  }
  
  if (!entryData.duration) {
    showToast('Please enter training duration', 'error');
    return;
  }
  
  if (!entryData.observations.trim()) {
    showToast('Please add some training notes', 'error');
    return;
  }

  try {
    const timestamp = new Date(`${entryData.date}T${entryData.time}`).toISOString();
    
    await setDoc(doc(collection(db, `users/${currentUser.uid}/dogs/${selectedDog.id}/entries`)), {
      activity: entryData.activity === 'Other' ? entryData.customActivity : entryData.activity,
      customActivity: entryData.customActivity,
      duration: entryData.duration,
      issues: entryData.issues,
      observations: entryData.observations,
      dogMood: entryData.dogMood,
      success: entryData.success,
      photos: entryData.photos,
      videoLink: entryData.videoLink,
      time: entryData.time,
      timestamp: timestamp,
      createdAt: new Date().toISOString()
    });

    await loadDogEntries(currentUser.uid, selectedDog.id);
    setEntryData({
      date: new Date().toISOString().split('T')[0],
      time: new Date().toTimeString().slice(0, 5),
      activity: '',
      customActivity: '',
      duration: '',
      issues: '',
      observations: '',
      dogMood: 'neutral',
      success: 'moderate',
      photos: [],
      videoLink: ''
    });
    setShowEntryForm(false);
    showToast('Training entry saved!', 'success');
  } catch (error) {
    console.error('Error saving entry:', error);
    showToast('Error saving entry: ' + error.message, 'error');
  }
  };

  const handleUpdateEntry = async () => {
  // Validation
  if (!entryData.activity) {
    showToast('Please select an activity', 'error');
    return;
  }
  
  if (entryData.activity === 'Other' && !entryData.customActivity.trim()) {
    showToast('Please specify the custom activity', 'error');
    return;
  }
  
  if (!entryData.duration) {
    showToast('Please enter training duration', 'error');
    return;
  }
  
  if (!entryData.observations.trim()) {
    showToast('Please add some training notes', 'error');
    return;
  }

  try {
    const timestamp = new Date(`${entryData.date}T${entryData.time}`).toISOString();
    
    await updateDoc(doc(db, `users/${currentUser.uid}/dogs/${selectedDog.id}/entries`, editingEntry.id), {
      activity: entryData.activity === 'Other' ? entryData.customActivity : entryData.activity,
      customActivity: entryData.customActivity,
      duration: entryData.duration,
      issues: entryData.issues,
      observations: entryData.observations,
      dogMood: entryData.dogMood,
      success: entryData.success,
      photos: entryData.photos,
      videoLink: entryData.videoLink,
      time: entryData.time,
      timestamp: timestamp
    });

    await loadDogEntries(currentUser.uid, selectedDog.id);
    setEntryData({
      date: new Date().toISOString().split('T')[0],
      time: new Date().toTimeString().slice(0, 5),
      activity: '',
      customActivity: '',
      duration: '',
      issues: '',
      observations: '',
      dogMood: 'neutral',
      success: 'moderate',
      photos: [],
      videoLink: ''
    });
    setEditingEntry(null);
    setShowEntryForm(false);
    showToast('Entry updated!', 'success');
  } catch (error) {
    console.error('Error updating entry:', error);
    showToast('Error updating entry: ' + error.message, 'error');
  }
  };

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(to bottom right, #fef3c7, #fed7aa)'
      }}>
        <div style={{ fontSize: '48px' }}>Loading...</div>
      </div>
    );
  }

  if (!currentUser) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(to bottom right, #fef3c7, #fed7aa)',
        padding: '20px'
      }}>
        {/* Error and success messages */}
        {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}

        <div style={{
          background: 'white',
          borderRadius: '12px',
          boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)',
          padding: '40px',
          maxWidth: '400px',
          width: '100%'
        }}>
          <div style={{ textAlign: 'center', marginBottom: '30px' }}>
            <img 
              src="https://methoddogtraining.com/wp-content/uploads/2023/02/Color-logo-no-background.png" 
              alt="Method Dog Training" 
              style={{ maxWidth: '200px', height: 'auto', marginBottom: '15px' }}
            />
            <h1 style={{ fontSize: '24px', fontWeight: 'bold', color: '#1f2937', marginBottom: '5px' }}>
              Training Journal
            </h1>
          </div>

          {showForgotPassword ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
              <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '10px' }}>Reset Password</h3>
              <input
                type="email"
                placeholder="Enter your email"
                value={resetEmail}
                onChange={(e) => setResetEmail(e.target.value)}
                style={{
                  width: '100%',
                  border: '1px solid #d1d5db',
                  borderRadius: '8px',
                  padding: '12px 16px'
                }}
              />
              <button
                onClick={handleForgotPassword}
                style={{
                  width: '100%',
                  background: '#d97706',
                  color: 'white',
                  padding: '12px 16px',
                  borderRadius: '8px',
                  border: 'none',
                  cursor: 'pointer',
                  fontWeight: '500'
                }}
              >
                Send Reset Email
              </button>
              <button
                onClick={() => setShowForgotPassword(false)}
                style={{
                  width: '100%',
                  background: 'transparent',
                  color: '#6b7280',
                  padding: '8px',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                Back to Login
              </button>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
              {loginData.isSignup && (
                <>
                  <input
                    type="text"
                    placeholder="Your Name"
                    value={loginData.name}
                    onChange={(e) => setLoginData({...loginData, name: e.target.value})}
                    style={{
                      width: '100%',
                      border: '1px solid #d1d5db',
                      borderRadius: '8px',
                      padding: '12px 16px'
                    }}
                  />
                </>
              )}

              <input
                type="email"
                placeholder="Email"
                value={loginData.email}
                onChange={(e) => setLoginData({...loginData, email: e.target.value})}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    handleLogin();
                  }
                }}
                style={{
                  width: '100%',
                  border: '1px solid #d1d5db',
                  borderRadius: '8px',
                  padding: '12px 16px'
                }}
              />

              <input
                type="password"
                placeholder="Password"
                value={loginData.password}
                onChange={(e) => setLoginData({...loginData, password: e.target.value})}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    handleLogin();
                  }
                }}
                style={{
                  width: '100%',
                  border: '1px solid #d1d5db',
                  borderRadius: '8px',
                  padding: '12px 16px'
                }}
              />

              {loginData.isSignup && (
                <div style={{
                  background: '#fffbeb',
                  border: '1px solid #fcd34d',
                  borderRadius: '8px',
                  padding: '15px',
                  marginTop: '5px'
                }}>
                  <label style={{
                    display: 'flex',
                    alignItems: 'start',
                    cursor: 'pointer',
                    fontSize: '13px',
                    lineHeight: '1.5',
                    color: '#1f2937'
                  }}>
                    <input
                      type="checkbox"
                      checked={loginData.agreeToTerms}
                      onChange={(e) => setLoginData({...loginData, agreeToTerms: e.target.checked})}
                      style={{
                        marginRight: '10px',
                        marginTop: '3px',
                        width: '16px',
                        height: '16px',
                        cursor: 'pointer',
                        flexShrink: 0
                      }}
                    />
                    <span>
                      I agree to the <a href="https://methoddogtraining.com/privacy-policy" target="_blank" rel="noopener noreferrer" style={{ color: '#d97706', textDecoration: 'underline' }}>Privacy Policy</a> and <a href="https://methoddogtraining.com/terms-of-service" target="_blank" rel="noopener noreferrer" style={{ color: '#d97706', textDecoration: 'underline' }}>Terms of Service</a> of Method Dog Training, which may change at any time. I understand I will receive journal-related reminders (customizable) and occasional dog training emails to help me on my journey. I can change preferences or unsubscribe at any time. My information will not be shared or sold.
                    </span>
                  </label>
                </div>
              )}

              <button
                onClick={handleLogin}
                style={{
                  width: '100%',
                  background: '#d97706',
                  color: 'white',
                  padding: '12px 16px',
                  borderRadius: '8px',
                  border: 'none',
                  cursor: 'pointer',
                  fontWeight: '500'
                }}
              >
                {loginData.isSignup ? 'Sign Up' : 'Login'}
              </button>

              <button
                onClick={() => setShowForgotPassword(true)}
                style={{
                  width: '100%',
                  background: 'transparent',
                  color: '#6b7280',
                  padding: '4px',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '13px',
                  textDecoration: 'underline'
                }}
              >
                Forgot Password?
              </button>

              <button
                onClick={() => setLoginData({...loginData, isSignup: !loginData.isSignup, agreeToTerms: !loginData.isSignup ? true : loginData.agreeToTerms})}
                style={{
                  width: '100%',
                  background: 'transparent',
                  color: '#d97706',
                  padding: '8px',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                {loginData.isSignup ? 'Already have an account? Login' : 'Need an account? Sign up'}
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }
  // Trainer Dashboard
  if (currentUser.accountType === 'trainer') {
    // Show loading state while data is being fetched
    if (loading) {
      return (
        <div style={{
          minHeight: '100vh',
          background: 'linear-gradient(to bottom right, #fef3c7, #fed7aa)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <div style={{
            background: 'white',
            borderRadius: '12px',
            padding: '40px',
            textAlign: 'center',
            boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)'
          }}>
            <h2 style={{ fontSize: '20px', fontWeight: 'bold', color: '#1f2937', marginBottom: '10px' }}>
              Loading clients...
            </h2>
            <p style={{ color: '#6b7280' }}>Please wait</p>
          </div>
        </div>
      );
    }
    
    // Ensure allClients is an array before mapping
    const clientsList = Array.isArray(allClients) ? allClients : [];
    
    // Process all clients with question counts
    const processedClients = clientsList.map(client => ({
      ...client,
      unansweredCount: (client.questions || []).filter(q => !q.answer).length,
      answeredCount: (client.questions || []).filter(q => q.answer).length,
      unansweredQuestions: (client.questions || []).filter(q => !q.answer).sort((a, b) => new Date(b.askedAt) - new Date(a.askedAt)),
      answeredQuestions: (client.questions || []).filter(q => q.answer).sort((a, b) => new Date(b.askedAt) - new Date(a.askedAt))
    }));

    const totalUnanswered = processedClients.reduce((sum, c) => sum + c.unansweredCount, 0);

    return (
      <div style={{
        minHeight: '100vh',
        background: 'linear-gradient(to bottom right, #fef3c7, #fed7aa)',
        padding: '20px'
      }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          {/* Header with Client Selector */}
          <div style={{
            background: 'white',
            borderRadius: '12px',
            boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
            padding: '30px',
            marginBottom: '20px'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <div>
                <h1 style={{ fontSize: '28px', fontWeight: 'bold', color: '#1f2937', margin: 0 }}>
                  Trainer Dashboard
                </h1>
                <p style={{ color: '#d97706', fontWeight: '500', fontSize: '14px', marginTop: '5px' }}>
                  Welcome, {currentUser.name}
                </p>
              </div>
              <div style={{ display: 'flex', gap: '10px' }}>
                <button
                  onClick={handleLogout}
                  style={{
                    background: '#ef4444',
                    color: 'white',
                    padding: '8px 16px',
                    borderRadius: '8px',
                    border: 'none',
                    cursor: 'pointer',
                    fontSize: '14px'
                  }}
                >
                  Logout
                </button>
              </div>
            </div>

            {/* Client Selector */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
              <label style={{ fontSize: '14px', fontWeight: '500', color: '#6b7280', whiteSpace: 'nowrap' }}>
                Select Client:
              </label>
              <select
                value={trainerSelectedClient?.uid || ''}
                onChange={(e) => {
                  const client = processedClients.find(c => c.uid === e.target.value);
                  setTrainerSelectedClient(client || null);
                  setTrainerActiveTab(client && client.unansweredCount > 0 ? 'unanswered' : 'summaries');
                }}
                style={{
                  flex: 1,
                  padding: '12px 16px',
                  border: '2px solid #d97706',
                  borderRadius: '8px',
                  fontSize: '15px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  background: 'white'
                }}
              >
                <option value="">📊 Overview - All Clients</option>
                {processedClients
                  .sort((a, b) => a.name.localeCompare(b.name))
                  .map(client => (
                    <option key={client.uid} value={client.uid}>
                      {client.name} {client.unansweredCount > 0 && `(${client.unansweredCount} new)`}
                    </option>
                  ))}
              </select>
            </div>
          </div>

          {/* Overview Mode */}
          {!trainerSelectedClient && (
            <div style={{
              background: 'white',
              borderRadius: '12px',
              boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
              padding: '30px'
            }}>
              <h2 style={{ fontSize: '20px', fontWeight: 'bold', color: '#1f2937', marginBottom: '10px' }}>
                Client Overview
              </h2>
              <p style={{ fontSize: '14px', color: '#6b7280', marginBottom: '25px' }}>
                Select a client above to view their details
              </p>

              {/* Stats */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px', marginBottom: '30px' }}>
                <div style={{
                  background: totalUnanswered > 0 ? '#fef3c7' : '#f3f4f6',
                  border: totalUnanswered > 0 ? '2px solid #fcd34d' : '2px solid #d1d5db',
                  borderRadius: '12px',
                  padding: '20px',
                  textAlign: 'center'
                }}>
                  <p style={{ fontSize: '36px', fontWeight: 'bold', color: totalUnanswered > 0 ? '#d97706' : '#6b7280', margin: '0 0 8px 0' }}>
                    {totalUnanswered}
                  </p>
                  <p style={{ fontSize: '14px', color: '#6b7280', margin: 0 }}>
                    Unanswered Questions
                  </p>
                </div>
                <div style={{
                  background: '#f3f4f6',
                  border: '2px solid #d1d5db',
                  borderRadius: '12px',
                  padding: '20px',
                  textAlign: 'center'
                }}>
                  <p style={{ fontSize: '36px', fontWeight: 'bold', color: '#6b7280', margin: '0 0 8px 0' }}>
                    {processedClients.length}
                  </p>
                  <p style={{ fontSize: '14px', color: '#6b7280', margin: 0 }}>
                    Total Clients
                  </p>
                </div>
              </div>

              {/* Clients with Unanswered Questions */}
              {totalUnanswered > 0 && (
                <div style={{ marginBottom: '30px' }}>
                  <h3 style={{ fontSize: '16px', fontWeight: 'bold', color: '#dc2626', marginBottom: '15px' }}>
                    ⚠️ Clients with Unanswered Questions
                  </h3>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    {processedClients
                      .filter(c => c.unansweredCount > 0)
                      .sort((a, b) => {
                        const aLatest = a.unansweredQuestions[0]?.askedAt || 0;
                        const bLatest = b.unansweredQuestions[0]?.askedAt || 0;
                        return new Date(bLatest) - new Date(aLatest);
                      })
                      .map(client => (
                        <div
                          key={client.uid}
                          onClick={() => {
                            setTrainerSelectedClient(client);
                            setTrainerActiveTab('unanswered');
                          }}
                          style={{
                            background: '#fef3c7',
                            border: '2px solid #fcd34d',
                            borderRadius: '8px',
                            padding: '15px',
                            cursor: 'pointer',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            transition: 'background 0.2s'
                          }}
                          onMouseOver={(e) => e.currentTarget.style.background = '#fde68a'}
                          onMouseOut={(e) => e.currentTarget.style.background = '#fef3c7'}
                        >
                          <div>
                            <p style={{ fontSize: '16px', fontWeight: 'bold', color: '#1f2937', margin: '0 0 4px 0' }}>
                              {client.name}
                            </p>
                            <p style={{ fontSize: '13px', color: '#6b7280', margin: 0 }}>
                              {client.email}
                            </p>
                          </div>
                          <div style={{
                            background: '#dc2626',
                            color: 'white',
                            padding: '6px 12px',
                            borderRadius: '12px',
                            fontSize: '14px',
                            fontWeight: 'bold'
                          }}>
                            {client.unansweredCount}
                          </div>
                        </div>
                      ))}
                  </div>
                </div>
              )}

              {/* All Clients Grid */}
              <div>
                <h3 style={{ fontSize: '16px', fontWeight: 'bold', color: '#1f2937', marginBottom: '15px' }}>
                  All Clients
                </h3>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', gap: '12px' }}>
                  {processedClients
                    .sort((a, b) => a.name.localeCompare(b.name))
                    .map(client => (
                      <div
                        key={client.uid}
                        onClick={() => {
                          setTrainerSelectedClient(client);
                          setTrainerActiveTab(client.unansweredCount > 0 ? 'unanswered' : 'summaries');
                        }}
                        style={{
                          background: 'white',
                          border: '2px solid #e5e7eb',
                          borderRadius: '8px',
                          padding: '15px',
                          cursor: 'pointer',
                          transition: 'all 0.2s'
                        }}
                        onMouseOver={(e) => {
                          e.currentTarget.style.borderColor = '#d97706';
                          e.currentTarget.style.background = '#fffbeb';
                        }}
                        onMouseOut={(e) => {
                          e.currentTarget.style.borderColor = '#e5e7eb';
                          e.currentTarget.style.background = 'white';
                        }}
                      >
                        <p style={{ fontSize: '15px', fontWeight: 'bold', color: '#1f2937', margin: '0 0 6px 0' }}>
                          {client.name}
                        </p>
                        <p style={{ fontSize: '12px', color: '#6b7280', margin: '0 0 10px 0' }}>
                          {client.dogs.length} dog{client.dogs.length !== 1 ? 's' : ''}
                        </p>
                        {client.unansweredCount > 0 && (
                          <div style={{
                            background: '#fee2e2',
                            color: '#dc2626',
                            padding: '4px 8px',
                            borderRadius: '6px',
                            fontSize: '11px',
                            fontWeight: '600',
                            display: 'inline-block'
                          }}>
                            {client.unansweredCount} new
                          </div>
                        )}
                      </div>
                    ))}
                </div>
              </div>
            </div>
          )}

          {/* Client Detail Mode */}
          {trainerSelectedClient && (
            <>
              {/* Tabs */}
              <div style={{
                background: 'white',
                borderRadius: '12px',
                boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                marginBottom: '20px',
                display: 'flex',
                overflow: 'hidden'
              }}>
                {['unanswered', 'answered', 'summaries'].map((tab) => {
                  const count = tab === 'unanswered' ? trainerSelectedClient.unansweredCount :
                                tab === 'answered' ? trainerSelectedClient.answeredCount :
                                (trainerSelectedClient.summaries || []).length;
                  
                  return (
                    <button
                      key={tab}
                      onClick={() => setTrainerActiveTab(tab)}
                      style={{
                        flex: 1,
                        padding: '15px',
                        border: 'none',
                        background: trainerActiveTab === tab ? '#fef3c7' : 'white',
                        borderBottom: trainerActiveTab === tab ? '3px solid #d97706' : '3px solid transparent',
                        cursor: 'pointer',
                        fontSize: '16px',
                        fontWeight: trainerActiveTab === tab ? '600' : '400',
                        color: trainerActiveTab === tab ? '#d97706' : '#6b7280',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '8px'
                      }}
                    >
                      {tab === 'unanswered' && '📬 Unanswered'}
                      {tab === 'answered' && '✅ Answered'}
                      {tab === 'summaries' && '📊 Weekly Summaries'}
                      
                      {count > 0 && tab === 'unanswered' && (
                        <span style={{
                          background: '#dc2626',
                          color: 'white',
                          padding: '2px 8px',
                          borderRadius: '12px',
                          fontSize: '12px',
                          fontWeight: 'bold'
                        }}>
                          {count}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>

              {/* Tab Content */}
              <div style={{
                background: 'white',
                borderRadius: '12px',
                boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
                padding: '30px'
              }}>
                {/* Client Info */}
                <div style={{ marginBottom: '25px', paddingBottom: '20px', borderBottom: '2px solid #f3f4f6' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '8px' }}>
                    <div style={{ flex: 1 }}>
                      <h2 style={{ fontSize: '20px', fontWeight: 'bold', color: '#1f2937', margin: '0 0 8px 0' }}>
                        {trainerSelectedClient.name}
                      </h2>
                      <p style={{ fontSize: '14px', color: '#6b7280', margin: '0 0 12px 0' }}>
                        {trainerSelectedClient.email}
                      </p>
                    </div>
                    <button
                      onClick={() => handleRemoveClient(trainerSelectedClient.uid)}
                      style={{
                        background: '#fee2e2',
                        color: '#dc2626',
                        border: '1px solid #fecaca',
                        padding: '6px 12px',
                        borderRadius: '6px',
                        fontSize: '12px',
                        fontWeight: '500',
                        cursor: 'pointer',
                        transition: 'all 0.2s'
                      }}
                      onMouseOver={(e) => {
                        e.currentTarget.style.background = '#fecaca';
                      }}
                      onMouseOut={(e) => {
                        e.currentTarget.style.background = '#fee2e2';
                      }}
                    >
                      🗑️ Remove Client
                    </button>
                  </div>
                  {trainerSelectedClient.dogs && trainerSelectedClient.dogs.length > 0 && (
                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                      {trainerSelectedClient.dogs.map(dog => {
                        const fixedStatus = dog.spayedNeutered === 'yes' ? '✂️ Fixed' : 
                                          dog.spayedNeutered === 'no' ? '❌ Not Fixed' : 
                                          '❓ Unknown';
                        return (
                          <div key={dog.id} style={{
                            background: '#fef3c7',
                            padding: '6px 12px',
                            borderRadius: '8px',
                            fontSize: '13px',
                            border: '1px solid #fcd34d'
                          }}>
                            <div style={{ fontWeight: 'bold', marginBottom: '2px' }}>
                              {dog.photo && (
  <img 
    src={dog.photo} 
    alt={dog.name}
    style={{
      width: '60px',
      height: '60px',
      borderRadius: '50%',
      objectFit: 'cover',
      marginRight: '6px'
    }}
  />
)}
                             {dog.name}
                            </div>
                            <div style={{ fontSize: '11px', color: '#92400e' }}>
                              {dog.breed} • {dog.age} years • {fixedStatus}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* Unanswered Tab */}
                {trainerActiveTab === 'unanswered' && (
                  <div>
                    <h3 style={{ fontSize: '18px', fontWeight: 'bold', color: '#1f2937', marginBottom: '20px' }}>
                      Unanswered Questions
                    </h3>
                    
                    {trainerSelectedClient.unansweredCount === 0 ? (
                      <p style={{ textAlign: 'center', color: '#6b7280', padding: '40px 0' }}>
                        🎉 All caught up! No unanswered questions.
                      </p>
                    ) : (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                        {trainerSelectedClient.unansweredQuestions.map((q) => (
                          <div
                            key={q.id}
                            style={{
                              background: '#fef3c7',
                              border: '2px solid #fcd34d',
                              borderRadius: '12px',
                              padding: '20px'
                            }}
                          >
                            <div style={{ marginBottom: '12px' }}>
                              <p style={{ fontSize: '16px', fontWeight: '600', color: '#1f2937', margin: '0 0 8px 0' }}>
                                {q.question}
                              </p>
                              <p style={{ fontSize: '13px', color: '#6b7280', margin: 0 }}>
                                🐕 {q.dogName} • 📅 {new Date(q.askedAt).toLocaleDateString()} at {new Date(q.askedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                              </p>
                            </div>

                            <div>
                              <textarea
                                placeholder="Type your answer here..."
                                id={`answer-${q.id}`}
                                style={{
                                  width: '100%',
                                  maxWidth: '100%',
                                  boxSizing: 'border-box',
                                  border: '2px solid #d1d5db',
                                  borderRadius: '8px',
                                  padding: '12px',
                                  fontSize: '14px',
                                  minHeight: '100px',
                                  resize: 'vertical',
                                  fontFamily: 'inherit'
                                }}
                              />
                              <button
                                onClick={() => {
                                  const answer = document.getElementById(`answer-${q.id}`).value;
                                  if (answer.trim()) {
                                    handleAnswerQuestion(trainerSelectedClient.uid, q.id, answer);
                                    document.getElementById(`answer-${q.id}`).value = '';
                                  } else {
                                    showToast('Please enter an answer', 'error');
                                  }
                                }}
                                style={{
                                  background: '#d97706',
                                  color: 'white',
                                  padding: '10px 20px',
                                  borderRadius: '8px',
                                  border: 'none',
                                  cursor: 'pointer',
                                  fontSize: '14px',
                                  fontWeight: '500',
                                  marginTop: '10px'
                                }}
                              >
                                📤 Send Answer
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Answered Tab */}
                {trainerActiveTab === 'answered' && (
                  <div>
                    <h3 style={{ fontSize: '18px', fontWeight: 'bold', color: '#1f2937', marginBottom: '20px' }}>
                      Answered Questions
                    </h3>
                    
                    {trainerSelectedClient.answeredCount === 0 ? (
                      <p style={{ textAlign: 'center', color: '#6b7280', padding: '40px 0' }}>
                        No answered questions yet.
                      </p>
                    ) : (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                        {trainerSelectedClient.answeredQuestions.map((q) => (
                          <div
                            key={q.id}
                            style={{
                              background: 'white',
                              border: '2px solid #e5e7eb',
                              borderRadius: '12px',
                              padding: '20px'
                            }}
                          >
                            <div style={{ marginBottom: '12px' }}>
                              <p style={{ fontSize: '16px', fontWeight: '600', color: '#1f2937', margin: '0 0 8px 0' }}>
                                {q.question}
                              </p>
                              <p style={{ fontSize: '13px', color: '#6b7280', margin: 0 }}>
                                🐕 {q.dogName} • 📅 Asked {new Date(q.askedAt).toLocaleDateString()}
                              </p>
                            </div>

                            <div style={{
                              background: '#dcfce7',
                              padding: '15px',
                              borderRadius: '8px',
                              borderLeft: '4px solid #10b981'
                            }}>
                              <p style={{ fontSize: '13px', fontWeight: '600', color: '#166534', marginBottom: '8px' }}>
                                Your Answer:
                              </p>
                              <p style={{ fontSize: '14px', color: '#166534', margin: 0, lineHeight: '1.6', whiteSpace: 'pre-wrap' }}>
                                {q.answer}
                              </p>
                              {q.answeredAt && (
                                <p style={{ fontSize: '12px', color: '#6b7280', marginTop: '10px', fontStyle: 'italic' }}>
                                  Answered {new Date(q.answeredAt).toLocaleDateString()}
                                </p>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Weekly Summaries Tab */}
                {trainerActiveTab === 'summaries' && (
                  <div>
                    <h3 style={{ fontSize: '18px', fontWeight: 'bold', color: '#1f2937', marginBottom: '20px' }}>
                      Weekly Training Summaries
                    </h3>
                    
                    {(() => {
                      const clientEntries = trainerSelectedClient.entries || [];
                      const clientSummaries = trainerSelectedClient.summaries || [];
                      
                      if (clientEntries.length === 0 && clientSummaries.length === 0) {
                        return (
                          <p style={{ textAlign: 'center', color: '#6b7280', padding: '40px 0' }}>
                            No training data or summaries yet from this client.
                          </p>
                        );
                      }

                      // Group entries by week AND dog
                      const weekDogMap = {};
                      clientEntries.forEach(entry => {
                        const entryDate = new Date(entry.timestamp);
                        const monday = new Date(entryDate);
                        const day = monday.getDay();
                        const diff = monday.getDate() - day + (day === 0 ? -6 : 1);
                        monday.setDate(diff);
                        monday.setHours(0, 0, 0, 0);
                        const weekKey = monday.toISOString().split('T')[0];
                        const dogKey = entry.dogId || 'unknown';
                        const combinedKey = `${weekKey}-${dogKey}`;
                        
                        if (!weekDogMap[combinedKey]) {
                          weekDogMap[combinedKey] = {
                            weekKey,
                            dogId: dogKey,
                            dogName: entry.dogName || 'Unknown Dog',
                            entries: []
                          };
                        }
                        weekDogMap[combinedKey].entries.push(entry);
                      });

                      // Create a map of existing summaries by weekStart
                      const summaryMap = {};
                      clientSummaries.forEach(summary => {
                        summaryMap[summary.weekStart] = summary;
                      });

                      // Generate summaries for ALL weeks with training entries (separated by dog)
                      const allSummaries = [];
                      
                      // Add auto-generated summaries for all week-dog combinations
                      Object.values(weekDogMap).forEach(weekDogData => {
                        const weekEntries = weekDogData.entries;
                        const existingSummary = summaryMap[weekDogData.weekKey];
                        
                        const stats = {
                          totalSessions: weekEntries.length,
                          totalMinutes: weekEntries.reduce((sum, e) => sum + (parseInt(e.duration) || 0), 0),
                          activities: {},
                          moods: { happy: 0, neutral: 0, frustrated: 0 },
                          success: { high: 0, moderate: 0, low: 0 }
                        };

                        weekEntries.forEach(e => {
                          stats.activities[e.activity] = (stats.activities[e.activity] || 0) + 1;
                          if (e.dogMood) stats.moods[e.dogMood]++;
                          if (e.success) stats.success[e.success]++;
                        });

                        allSummaries.push({
                          id: `${weekDogData.weekKey}-${weekDogData.dogId}`,
                          weekStart: weekDogData.weekKey,
                          dogName: weekDogData.dogName,
                          dogId: weekDogData.dogId,
                          summary: existingSummary?.summary || null,
                          createdAt: existingSummary?.createdAt || null,
                          stats
                        });
                      });

                      // Sort by week (newest first), then by dog name
                      allSummaries.sort((a, b) => {
                        const weekCompare = new Date(b.weekStart) - new Date(a.weekStart);
                        if (weekCompare !== 0) return weekCompare;
                        return (a.dogName || '').localeCompare(b.dogName || '');
                      });

                      if (allSummaries.length === 0) {
                        return (
                          <p style={{ textAlign: 'center', color: '#6b7280', padding: '40px 0' }}>
                            No training data yet from this client.
                          </p>
                        );
                      }

                      return (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                          {allSummaries.map((summary) => (
                            <div
                              key={summary.id}
                              style={{
                                border: '2px solid #e5e7eb',
                                borderRadius: '12px',
                                padding: '20px',
                                background: 'white'
                              }}
                            >
                              <div style={{ marginBottom: '15px', paddingBottom: '12px', borderBottom: '2px solid #f3f4f6' }}>
                                {/* Dog name badge */}
                                <div style={{
                                  background: '#fef3c7',
                                  border: '1px solid #fcd34d',
                                  borderRadius: '8px',
                                  padding: '6px 12px',
                                  display: 'inline-block',
                                  marginBottom: '10px'
                                }}>
                                  <span style={{ fontSize: '14px', fontWeight: 'bold', color: '#92400e' }}>
                                    🐕 {summary.dogName}
                                  </span>
                                </div>
                                
                                <h4 style={{ fontSize: '16px', fontWeight: 'bold', color: '#1f2937', margin: '0 0 4px 0' }}>
                                  Week: {new Date(summary.weekStart).toLocaleDateString()} - {new Date(new Date(summary.weekStart).getTime() + 6 * 24 * 60 * 60 * 1000).toLocaleDateString()}
                                </h4>
                                <p style={{ fontSize: '12px', color: '#9ca3af', margin: 0 }}>
                                  {summary.createdAt ? `Submitted ${new Date(summary.createdAt).toLocaleDateString()}` : 'Auto-generated'}
                                </p>
                              </div>

                              {/* Training Stats */}
                              {summary.stats && summary.stats.totalSessions > 0 && (
                                <div style={{
                                  background: '#f0fdf4',
                                  border: '1px solid #86efac',
                                  borderRadius: '8px',
                                  padding: '15px',
                                  marginBottom: summary.summary ? '15px' : '0'
                                }}>
                                  <h5 style={{ fontSize: '14px', fontWeight: 'bold', color: '#166534', marginBottom: '12px' }}>
                                    📊 Training Statistics
                                  </h5>
                                  
                                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: '10px', marginBottom: '12px' }}>
                                    <div style={{ background: 'white', padding: '10px', borderRadius: '6px', textAlign: 'center' }}>
                                      <p style={{ fontSize: '22px', fontWeight: 'bold', color: '#d97706', margin: '0 0 4px 0' }}>
                                        {summary.stats.totalSessions}
                                      </p>
                                      <p style={{ fontSize: '11px', color: '#6b7280', margin: 0 }}>Sessions</p>
                                    </div>
                                    <div style={{ background: 'white', padding: '10px', borderRadius: '6px', textAlign: 'center' }}>
                                      <p style={{ fontSize: '22px', fontWeight: 'bold', color: '#d97706', margin: '0 0 4px 0' }}>
                                        {summary.stats.totalMinutes}
                                      </p>
                                      <p style={{ fontSize: '11px', color: '#6b7280', margin: 0 }}>Minutes</p>
                                    </div>
                                  </div>

                                  {Object.keys(summary.stats.activities || {}).length > 0 && (
                                    <div style={{ marginBottom: '10px' }}>
                                      <p style={{ fontSize: '12px', fontWeight: '600', color: '#166534', marginBottom: '6px' }}>
                                        Activities:
                                      </p>
                                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                                        {Object.entries(summary.stats.activities).map(([activity, count]) => (
                                          <span key={activity} style={{
                                            background: 'white',
                                            padding: '4px 8px',
                                            borderRadius: '4px',
                                            fontSize: '11px'
                                          }}>
                                            {activity} ({count}x)
                                          </span>
                                        ))}
                                      </div>
                                    </div>
                                  )}

                                  {Object.values(summary.stats.moods || {}).some(v => v > 0) && (
                                    <div style={{ marginBottom: '10px' }}>
                                      <p style={{ fontSize: '12px', fontWeight: '600', color: '#166534', marginBottom: '6px' }}>
                                        Mood:
                                      </p>
                                      <div style={{ display: 'flex', gap: '8px', fontSize: '11px' }}>
                                        {summary.stats.moods.happy > 0 && <span>😊 Happy: {summary.stats.moods.happy}</span>}
                                        {summary.stats.moods.neutral > 0 && <span>😐 Neutral: {summary.stats.moods.neutral}</span>}
                                        {summary.stats.moods.frustrated > 0 && <span>😤 Frustrated: {summary.stats.moods.frustrated}</span>}
                                      </div>
                                    </div>
                                  )}

                                  {Object.values(summary.stats.success || {}).some(v => v > 0) && (
                                    <div>
                                      <p style={{ fontSize: '12px', fontWeight: '600', color: '#166534', marginBottom: '6px' }}>
                                        Success Rate:
                                      </p>
                                      <div style={{ display: 'flex', gap: '8px', fontSize: '11px' }}>
                                        {summary.stats.success.high > 0 && <span>✅ High: {summary.stats.success.high}</span>}
                                        {summary.stats.success.moderate > 0 && <span>➖ Moderate: {summary.stats.success.moderate}</span>}
                                        {summary.stats.success.low > 0 && <span>⚠️ Low: {summary.stats.success.low}</span>}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              )}

                              {/* Client's Notes */}
                              {summary.summary && (
                                <div style={{
                                  background: '#fffbeb',
                                  border: '1px solid #fcd34d',
                                  borderRadius: '8px',
                                  padding: '15px'
                                }}>
                                  <h5 style={{ fontSize: '14px', fontWeight: 'bold', color: '#92400e', marginBottom: '8px' }}>
                                    💭 Client's Reflection & Notes
                                  </h5>
                                  <p style={{ fontSize: '14px', lineHeight: '1.6', color: '#374151', whiteSpace: 'pre-wrap', margin: 0 }}>
                                    {summary.summary}
                                  </p>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      );
                    })()}
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        {/* Tips Modal */}
        {showTipsModal && (
          <TipsModal onClose={() => setShowTipsModal(false)} />
        )}
        
        {/* Settings Modal */}
        {showSettings && (
          <UserSettings
            currentUser={currentUser}
            db={db}
            onClose={() => setShowSettings(false)}
            showToast={showToast}
            onDeleteAccount={handleDeleteAccount}
          />
        )}

        <Footer />
      </div>
    );
  }

  // Client Dashboard
  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(to bottom right, #fef3c7, #fed7aa)'
    }}>
      {/* Toast notifications - Global for logged in users */}
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}

      {/* Header */}
      <div style={{
        background: 'white',
        borderRadius: '0 0 12px 12px',
        boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
        padding: '20px',
        marginBottom: '20px'
      }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h1 style={{ fontSize: '24px', fontWeight: 'bold', color: '#1f2937', margin: 0 }}>
            Training Journal
          </h1>
          <div style={{ display: 'flex', gap: '10px' }}>
            <button
              onClick={() => setShowTipsModal(true)}
              style={{
                background: '#fcd34d',
                color: '#92400e',
                padding: '8px 16px',
                borderRadius: '8px',
                border: 'none',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '500'
              }}
            >
              💡 Tips
            </button>
            <button
              onClick={() => setShowSettings(true)}
              style={{
                background: '#f3f4f6',
                color: '#374151',
                padding: '8px 16px',
                borderRadius: '8px',
                border: 'none',
                cursor: 'pointer',
                fontSize: '14px'
              }}
            >
              ⚙️ Settings
            </button>
            <button
              onClick={handleLogout}
              style={{
                background: '#ef4444',
                color: 'white',
                padding: '8px 16px',
                borderRadius: '8px',
                border: 'none',
                cursor: 'pointer',
                fontSize: '14px'
              }}
            >
              Logout
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '20px' }}>
        
        {/* Dog Selector */}
        {dogs.length > 0 && (
          <div style={{
            background: 'white',
            borderRadius: '12px',
            boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
            padding: '20px',
            marginBottom: '20px'
          }}>
            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center' }}>
              <span style={{ fontWeight: '500', fontSize: '16px' }}>Select Dog:</span>
              {dogs.map((dog) => (
                <button
                  key={dog.id}
                  onClick={() => {
                    setSelectedDog(dog);
                    loadDogEntries(currentUser.uid, dog.id);
                  }}
                  style={{
                    padding: '5px 10px',
                    borderRadius: '8px',
                    border: selectedDog?.id === dog.id ? '2px solid #d97706' : '2px solid #e5e7eb',
                    background: selectedDog?.id === dog.id ? '#fef3c7' : 'white',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: selectedDog?.id === dog.id ? '600' : '400',
                    whiteSpace: 'nowrap',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                >
                  {dog.photo && (
                    <img 
                      src={dog.photo} 
                      alt={dog.name}
                      style={{
                        width: '35px',
                        height: '35px',
                        borderRadius: '50%',
                        objectFit: 'cover',
                        border: '1px solid #d1d5db'
                      }}
                    />
                  )}
                  <span>{dog.name}</span>
                  
                  {/* Edit icon inside button */}
                  <span
                    onClick={(e) => {
                      e.stopPropagation();
                      setEditingDog(dog);
                      setDogFormData({
                        name: dog.name,
                        breed: dog.breed,
                        age: dog.age,
                        spayedNeutered: dog.spayedNeutered || 'unknown',
                        photo: dog.photo || null
                      });
                      setShowDogForm(true);
                    }}
                    style={{
                      cursor: 'pointer',
                      fontSize: '14px',
                      display: 'flex',
                      alignItems: 'center',
                      marginLeft: '4px'
                    }}
                    title="Edit dog"
                  >
                    ✏️
                  </span>
                </button>
              ))}
              <button
                onClick={() => setShowDogForm(true)}
                style={{
                  padding: '10px 20px',
                  borderRadius: '8px',
                  border: '2px dashed #d97706',
                  background: 'white',
                  cursor: 'pointer',
                  fontSize: '14px',
                  color: '#d97706',
                  fontWeight: '500'
                }}
              >
                + Add Dog
              </button>
            </div>
          </div>
        )}

        {/* No Dogs Yet */}
        {dogs.length === 0 && (
          <div style={{
            background: 'white',
            borderRadius: '12px',
            boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
            padding: '40px',
            textAlign: 'center',
            marginBottom: '20px'
          }}>
            <h2 style={{ fontSize: '20px', marginBottom: '10px' }}>Add Your First Dog!</h2>
            <p style={{ color: '#6b7280', marginBottom: '20px' }}>
              Get started by adding your dog's profile
            </p>
            <button
              onClick={() => setShowDogForm(true)}
              style={{
                background: '#d97706',
                color: 'white',
                padding: '12px 24px',
                borderRadius: '8px',
                border: 'none',
                cursor: 'pointer',
                fontSize: '16px',
                fontWeight: '500'
              }}
            >
              Add Dog
            </button>
          </div>
        )}

        {/* Tabs */}
        {selectedDog && (
          <>
            <div style={{
              background: 'white',
              borderRadius: '12px',
              boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
              marginBottom: '20px',
              display: 'flex',
              overflow: 'hidden'
            }}>
              {['log', 'weekly', 'questions'].map((tab) => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  style={{
                    flex: 1,
                    padding: '15px',
                    border: 'none',
                    background: activeTab === tab ? '#fef3c7' : 'white',
                    borderBottom: activeTab === tab ? '3px solid #d97706' : '3px solid transparent',
                    cursor: 'pointer',
                    fontSize: '16px',
                    fontWeight: activeTab === tab ? '600' : '400',
                    color: activeTab === tab ? '#d97706' : '#6b7280',
                    position: 'relative'
                  }}
                >
                  {tab === 'log' && '📝 Training Log'}
                  {tab === 'weekly' && '📊 Weekly Summary'}
                  {tab === 'questions' && '❓ Ask Trainer'}
                  
                  {/* Notification badges for questions tab only */}
                  {tab === 'questions' && (
                    <>
                      {/* Unanswered badge (red) - positioned on the right */}
                      {questions.filter(q => !q.answer).length > 0 && (
                        <span style={{
                          position: 'absolute',
                          top: '8px',
                          right: '8px',
                          background: '#ef4444',
                          color: 'white',
                          borderRadius: '10px',
                          padding: '2px 6px',
                          fontSize: '11px',
                          fontWeight: 'bold',
                          minWidth: '18px',
                          textAlign: 'center'
                        }}>
                          {questions.filter(q => !q.answer).length}
                        </span>
                      )}
                      
                      {/* Answered badge (green) - positioned on the right below unanswered */}
                      {questions.filter(q => q.answer).length > 0 && (
                        <span style={{
                          position: 'absolute',
                          top: '32px',
                          right: '8px',
                          background: '#10b981',
                          color: 'white',
                          borderRadius: '10px',
                          padding: '2px 6px',
                          fontSize: '11px',
                          fontWeight: 'bold',
                          minWidth: '18px',
                          textAlign: 'center'
                        }}>
                          {questions.filter(q => q.answer).length}
                        </span>
                      )}
                    </>
                  )}
                </button>
              ))}


            </div>

            {/* Training Log Tab */}
            {activeTab === 'log' && (
              <div style={{
                background: 'white',
                borderRadius: '12px',
                boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                padding: '30px'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                  <h2 style={{ fontSize: '20px', fontWeight: 'bold', margin: 0 }}>
                    Training Entries for {selectedDog.name}
                  </h2>
                  <button
                    onClick={() => setShowEntryForm(true)}
                    style={{
                      background: '#d97706',
                      color: 'white',
                      padding: '10px 20px',
                      borderRadius: '8px',
                      border: 'none',
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontWeight: '500'
                    }}
                  >
                    + New Entry
                  </button>
                </div>

                {entries.length === 0 ? (
                  <p style={{ textAlign: 'center', color: '#6b7280', padding: '40px 0' }}>
                    No training entries yet. Click "New Entry" to get started!
                  </p>
                ) : (
                  <div style={{ display: 'grid', gap: '15px' }}>
                    {entries.map((entry) => (
                      <div
                        key={entry.id}
                        style={{
                          border: '1px solid #e5e7eb',
                          borderRadius: '8px',
                          padding: '15px',
                          position: 'relative'
                        }}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px' }}>
                          <div>
                            <p style={{ fontSize: '14px', fontWeight: 'bold', margin: '0 0 5px 0' }}>
                              {new Date(entry.timestamp).toLocaleDateString()} at {entry.time}
                            </p>
                            <p style={{ fontSize: '16px', fontWeight: '500', color: '#d97706', margin: 0 }}>
                              {entry.activity}
                            </p>
                          </div>
                          <div style={{ display: 'flex', gap: '5px' }}>
                            <button
                              onClick={() => {
                                setEditingEntry(entry);
                                setEntryData({
                                  date: new Date(entry.timestamp).toISOString().split('T')[0],
                                  time: entry.time,
                                  activity: entry.activity,
                                  customActivity: entry.customActivity || '',
                                  duration: entry.duration,
                                  issues: entry.issues || '',
                                  observations: entry.observations || '',
                                  dogMood: entry.dogMood || 'neutral',
                                  success: entry.success || 'moderate',
                                  photos: entry.photos || [],
                                  videoLink: entry.videoLink || ''
                                });
                                setShowEntryForm(true);
                              }}
                              style={{
                                background: '#f3f4f6',
                                border: 'none',
                                borderRadius: '6px',
                                padding: '5px 10px',
                                cursor: 'pointer',
                                fontSize: '12px'
                              }}
                            >
                              Edit
                            </button>
                            <button
                              onClick={() => handleDeleteEntry(entry.id)}
                              style={{
                                background: '#fee2e2',
                                color: '#dc2626',
                                border: 'none',
                                borderRadius: '6px',
                                padding: '5px 10px',
                                cursor: 'pointer',
                                fontSize: '12px'
                              }}
                            >
                              Delete
                            </button>
                          </div>
                        </div>
                        <p style={{ fontSize: '14px', margin: '5px 0', color: '#6b7280' }}>
                          Duration: {entry.duration} minutes
                        </p>
                        {entry.observations && (
                          <p style={{ fontSize: '14px', margin: '10px 0 5px 0' }}>
                            <strong>Notes:</strong> {entry.observations}
                          </p>
                        )}
                        {entry.issues && (
                          <p style={{ fontSize: '14px', color: '#dc2626', margin: '5px 0' }}>
                            <strong>Issues:</strong> {entry.issues}
                          </p>
                        )}
                        <div style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
                          <span style={{
                            padding: '4px 10px',
                            borderRadius: '6px',
                            fontSize: '12px',
                            background: entry.dogMood === 'happy' ? '#dcfce7' : entry.dogMood === 'anxious' ? '#fef3c7' : '#fee2e2',
                            color: entry.dogMood === 'happy' ? '#166534' : entry.dogMood === 'anxious' ? '#92400e' : '#991b1b'
                          }}>
                            Mood: {entry.dogMood}
                          </span>
                          <span style={{
                            padding: '4px 10px',
                            borderRadius: '6px',
                            fontSize: '12px',
                            background: entry.success === 'excellent' ? '#dcfce7' : entry.success === 'moderate' ? '#fef3c7' : '#fee2e2',
                            color: entry.success === 'excellent' ? '#166534' : entry.success === 'moderate' ? '#92400e' : '#991b1b'
                          }}>
                            Success: {entry.success}
                          </span>
                        </div>
                        {entry.photos && entry.photos.length > 0 && (
                          <div style={{ marginTop: '10px', display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                            {entry.photos.map((photo, idx) => (
                              <img
                                key={idx}
                                src={photo}
                                alt={`Training photo ${idx + 1}`}
                                style={{
                                  width: '100px',
                                  height: '100px',
                                  objectFit: 'cover',
                                  borderRadius: '8px',
                                  border: '2px solid #e5e7eb'
                                }}
                              />
                            ))}
                          </div>
                        )}
                        {entry.videoLink && (
                          <div style={{ marginTop: '10px' }}>
                            
                              href={entry.videoLink}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{ color: '#d97706', fontSize: '14px' }}
                            <a>
                              🎥 View Video
                            </a>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Questions Tab */}
            {activeTab === 'questions' && (
              <div style={{
                background: 'white',
                borderRadius: '12px',
                boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                padding: '30px'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                  <h2 style={{ fontSize: '20px', fontWeight: 'bold', margin: 0 }}>
                    Ask Your Trainer
                  </h2>
                  <button
                    onClick={() => setShowQuestionForm(true)}
                    style={{
                      background: '#d97706',
                      color: 'white',
                      padding: '10px 20px',
                      borderRadius: '8px',
                      border: 'none',
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontWeight: '500'
                    }}
                  >
                    + Ask Question
                  </button>
                </div>

                {questions.length === 0 ? (
                  <p style={{ textAlign: 'center', color: '#6b7280', padding: '40px 0' }}>
                    No questions yet. Ask your trainer anything about your dog's training!
                  </p>
                ) : (
                  <div style={{ display: 'grid', gap: '15px' }}>
                    {questions.map((q) => (
                      <div
                        key={q.id}
                        style={{
                          border: `2px solid ${q.answer ? '#d1fae5' : '#fcd34d'}`,
                          borderRadius: '8px',
                          padding: '15px',
                          background: q.answer ? '#f0fdf4' : '#fef3c7'
                        }}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px' }}>
                          <p style={{ fontSize: '14px', color: '#6b7280', margin: 0 }}>
                            About: {q.dogName} • {new Date(q.askedAt).toLocaleDateString()}
                          </p>
                          <span style={{
                            padding: '4px 10px',
                            borderRadius: '6px',
                            fontSize: '12px',
                            background: q.answer ? '#dcfce7' : '#fef3c7',
                            color: q.answer ? '#166534' : '#92400e',
                            fontWeight: '500'
                          }}>
                            {q.answer ? '✓ Answered' : '⏳ Pending'}
                          </span>
                        </div>
                        <p style={{ fontSize: '16px', fontWeight: '500', margin: '10px 0' }}>
                          {q.question}
                        </p>
                        {q.answer && (
                          <div style={{
                            background: 'white',
                            padding: '12px',
                            borderRadius: '6px',
                            marginTop: '10px',
                            borderLeft: '4px solid #10b981'
                          }}>
                            <p style={{ fontSize: '13px', fontWeight: '600', color: '#059669', marginBottom: '5px' }}>
                              Trainer's Answer:
                            </p>
                            <p style={{ fontSize: '14px', margin: 0 }}>
                              {q.answer}
                            </p>
                            <p style={{ fontSize: '12px', color: '#6b7280', marginTop: '8px' }}>
                              Answered by {q.answeredBy} on {new Date(q.answeredAt).toLocaleDateString()}
                            </p>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Weekly Summary Tab */}
            {activeTab === 'weekly' && (
              <div style={{
                background: 'white',
                borderRadius: '12px',
                boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                padding: '30px'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                  <h2 style={{ fontSize: '20px', fontWeight: 'bold', margin: 0 }}>
                    Weekly Training Summaries
                  </h2>
                </div>

                {(() => {
                  // Auto-generate weekly summaries from entries
                  const generateWeeklySummaries = () => {
                    if (entries.length === 0) return [];

                    // Group entries by week (Sunday is end of week)
                    const weekMap = {};
                    
                    entries.forEach(entry => {
                      const entryDate = new Date(entry.timestamp);
                      
                      // Find the Monday of this entry's week
                      const monday = new Date(entryDate);
                      const day = monday.getDay();
                      const diff = monday.getDate() - day + (day === 0 ? -6 : 1);
                      monday.setDate(diff);
                      monday.setHours(0, 0, 0, 0);
                      
                      const weekKey = monday.toISOString().split('T')[0];
                      
                      if (!weekMap[weekKey]) {
                        weekMap[weekKey] = [];
                      }
                      weekMap[weekKey].push(entry);
                    });

                    // Create summaries for each week
                    return Object.entries(weekMap)
                      .map(([weekStart, weekEntries]) => {
                        const weekEnd = new Date(weekStart);
                        weekEnd.setDate(weekEnd.getDate() + 6); // Sunday

                        // Calculate stats
                        const stats = {
                          totalSessions: weekEntries.length,
                          totalMinutes: weekEntries.reduce((sum, e) => sum + (parseInt(e.duration) || 0), 0),
                          activities: {},
                          moods: { happy: 0, neutral: 0, frustrated: 0 },
                          success: { high: 0, moderate: 0, low: 0 },
                          issues: []
                        };

                        weekEntries.forEach(e => {
                          stats.activities[e.activity] = (stats.activities[e.activity] || 0) + 1;
                          if (e.dogMood) stats.moods[e.dogMood]++;
                          if (e.success) stats.success[e.success]++;
                          if (e.issues && e.issues.trim()) stats.issues.push(e.issues);
                        });

                        // Find if user has added notes for this week
                        const savedSummary = weeklySummaries.find(s => s.weekStart === weekStart);

                        return {
                          weekStart,
                          weekEnd: weekEnd.toISOString().split('T')[0],
                          stats,
                          summary: savedSummary?.summary || '',
                          id: savedSummary?.id || null,
                          createdAt: savedSummary?.createdAt || null
                        };
                      })
                      .sort((a, b) => new Date(b.weekStart) - new Date(a.weekStart));
                  };

                  const autoSummaries = generateWeeklySummaries();

                  return autoSummaries.length === 0 ? (
                    <p style={{ textAlign: 'center', color: '#6b7280', padding: '40px 0' }}>
                      No training entries yet. Add some training sessions to see your weekly summaries!
                    </p>
                  ) : (
                    <div style={{ display: 'grid', gap: '20px' }}>
                      {autoSummaries.map((summary, idx) => (
                        <div
                          key={`${summary.weekStart}-${idx}`}
                          style={{
                            border: '2px solid #e5e7eb',
                            borderRadius: '12px',
                            padding: '20px',
                            background: 'white'
                          }}
                        >
                          <div style={{ marginBottom: '15px', paddingBottom: '15px', borderBottom: '2px solid #f3f4f6' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                              <div>
                                <h3 style={{ fontSize: '16px', fontWeight: 'bold', color: '#1f2937', margin: '0 0 4px 0' }}>
                                  Week: {new Date(summary.weekStart).toLocaleDateString()} - {new Date(summary.weekEnd).toLocaleDateString()}
                                </h3>
                                <p style={{ fontSize: '12px', color: '#9ca3af', margin: 0 }}>
                                  {summary.createdAt ? `Notes added ${new Date(summary.createdAt).toLocaleDateString()}` : 'Auto-generated from training entries'}
                                </p>
                              </div>
                              <button
                                onClick={() => {
                                  setWeeklyData({ 
                                    summary: summary.summary, 
                                    weekStart: summary.weekStart,
                                    existingId: summary.id 
                                  });
                                  setShowWeeklySummary(true);
                                }}
                                style={{
                                  background: summary.summary ? '#f3f4f6' : '#d97706',
                                  color: summary.summary ? '#374151' : 'white',
                                  padding: '8px 16px',
                                  borderRadius: '8px',
                                  border: 'none',
                                  cursor: 'pointer',
                                  fontSize: '13px',
                                  fontWeight: '500',
                                  whiteSpace: 'nowrap'
                                }}
                              >
                                {summary.summary ? '✏️ Edit Notes' : '💭 Add Notes'}
                              </button>
                            </div>
                          </div>

                          {/* Training Stats */}
                          {summary.stats && summary.stats.totalSessions > 0 && (
                            <div style={{
                              background: '#f0fdf4',
                              border: '1px solid #86efac',
                              borderRadius: '8px',
                              padding: '15px',
                              marginBottom: summary.summary ? '15px' : '0'
                            }}>
                              <h4 style={{ fontSize: '14px', fontWeight: 'bold', color: '#166534', marginBottom: '12px' }}>
                                📊 Training Statistics
                              </h4>
                              
                              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: '10px', marginBottom: '12px' }}>
                                <div style={{ background: 'white', padding: '8px', borderRadius: '6px', textAlign: 'center' }}>
                                  <p style={{ fontSize: '20px', fontWeight: 'bold', color: '#d97706', margin: '0 0 2px 0' }}>
                                    {summary.stats.totalSessions}
                                  </p>
                                  <p style={{ fontSize: '11px', color: '#6b7280', margin: 0 }}>Sessions</p>
                                </div>
                                <div style={{ background: 'white', padding: '8px', borderRadius: '6px', textAlign: 'center' }}>
                                  <p style={{ fontSize: '20px', fontWeight: 'bold', color: '#d97706', margin: '0 0 2px 0' }}>
                                    {summary.stats.totalMinutes}
                                  </p>
                                  <p style={{ fontSize: '11px', color: '#6b7280', margin: 0 }}>Minutes</p>
                                </div>
                              </div>

                              {Object.keys(summary.stats.activities).length > 0 && (
                                <div style={{ marginBottom: '10px' }}>
                                  <p style={{ fontSize: '12px', fontWeight: '600', color: '#166534', marginBottom: '6px' }}>
                                    Activities:
                                  </p>
                                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                                    {Object.entries(summary.stats.activities).map(([activity, count]) => (
                                      <span key={activity} style={{
                                        background: 'white',
                                        padding: '4px 8px',
                                        borderRadius: '4px',
                                        fontSize: '11px'
                                      }}>
                                        {activity} ({count}x)
                                      </span>
                                    ))}
                                  </div>
                                </div>
                              )}

                              {Object.values(summary.stats.moods || {}).some(v => v > 0) && (
                                <div style={{ marginBottom: '10px' }}>
                                  <p style={{ fontSize: '12px', fontWeight: '600', color: '#166534', marginBottom: '6px' }}>
                                    Mood:
                                  </p>
                                  <div style={{ display: 'flex', gap: '8px', fontSize: '11px' }}>
                                    {summary.stats.moods.happy > 0 && <span>😊 {summary.stats.moods.happy}</span>}
                                    {summary.stats.moods.neutral > 0 && <span>😐 {summary.stats.moods.neutral}</span>}
                                    {summary.stats.moods.frustrated > 0 && <span>😤 {summary.stats.moods.frustrated}</span>}
                                  </div>
                                </div>
                              )}

                              {Object.values(summary.stats.success || {}).some(v => v > 0) && (
                                <div>
                                  <p style={{ fontSize: '12px', fontWeight: '600', color: '#166534', marginBottom: '6px' }}>
                                    Success Rate:
                                  </p>
                                  <div style={{ display: 'flex', gap: '8px', fontSize: '11px' }}>
                                    {summary.stats.success.high > 0 && <span>✅ High: {summary.stats.success.high}</span>}
                                    {summary.stats.success.moderate > 0 && <span>➖ Moderate: {summary.stats.success.moderate}</span>}
                                    {summary.stats.success.low > 0 && <span>⚠️ Low: {summary.stats.success.low}</span>}
                                  </div>
                                </div>
                              )}
                            </div>
                          )}

                          {/* Personal Notes */}
                          {summary.summary && (
                            <div style={{
                              background: '#fffbeb',
                              border: '1px solid #fcd34d',
                              borderRadius: '8px',
                              padding: '15px'
                            }}>
                              <h4 style={{ fontSize: '14px', fontWeight: 'bold', color: '#92400e', marginBottom: '8px' }}>
                                💭 Your Reflection & Notes
                              </h4>
                              <p style={{ fontSize: '14px', lineHeight: '1.6', color: '#374151', whiteSpace: 'pre-wrap', margin: 0 }}>
                                {summary.summary}
                              </p>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  );
                })()}
              </div>
            )}
          </>
        )}
      </div>

      {/* Add Dog Modal */}
      {showDogForm && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '20px',
          zIndex: 1000
        }}>
          <div style={{
            background: 'white',
            borderRadius: '12px',
            padding: '30px',
            maxWidth: '500px',
            width: '100%',
            maxHeight: '90vh',
            overflowY: 'auto'
          }}>
            <h3 style={{ fontSize: '20px', fontWeight: 'bold', marginBottom: '20px' }}>
  {editingDog ? 'Edit Dog Profile' : 'Add New Dog'}</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
              <input
                type="text"
                placeholder="Dog's Name"
                value={dogFormData.name}
                onChange={(e) => setDogFormData({...dogFormData, name: e.target.value})}
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '8px',
                  fontSize: '14px'
                }}
              />
              <input
                type="text"
                placeholder="Breed"
                value={dogFormData.breed}
                onChange={(e) => setDogFormData({...dogFormData, breed: e.target.value})}
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '8px',
                  fontSize: '14px'
                }}
              />
              <input
                type="text"
                placeholder="Age"
                value={dogFormData.age}
                onChange={(e) => setDogFormData({...dogFormData, age: e.target.value})}
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '8px',
                  fontSize: '14px'
                }}
              />
              <select
                value={dogFormData.spayedNeutered}
                onChange={(e) => setDogFormData({...dogFormData, spayedNeutered: e.target.value})}
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '8px',
                  fontSize: '14px'
                }}
              >
                <option value="unknown">Spayed/Neutered Status</option>
                <option value="yes">Yes</option>
                <option value="no">No</option>
                <option value="unknown">Unknown</option>
              </select>
              <input
                type="file"
                accept="image/*"
                onChange={handlePhotoUpload}
                style={{ fontSize: '14px' }}
              />
              {dogFormData.photo && (
                <img
                  src={dogFormData.photo}
                  alt="Dog preview"
                  style={{
                    width: '100px',
                    height: '100px',
                    objectFit: 'cover',
                    borderRadius: '8px'
                  }}
                />
              )}
              <div style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
                <button
                  onClick={editingDog ? handleEditDog : handleAddDog}
                  style={{
                    flex: 1,
                    background: '#d97706',
                    color: 'white',
                    padding: '12px',
                    borderRadius: '8px',
                    border: 'none',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '500'
                  }}
                >
                  {editingDog ? 'Update Dog' : 'Add Dog'}
                </button>
                <button
                  onClick={() => {
                    setShowDogForm(false);
                    setEditingDog(null);
                    setDogFormData({ name: '', breed: '', age: '', spayedNeutered: 'unknown', photo: null });
                  }}
                  style={{
                    flex: 1,
                    background: '#f3f4f6',
                    color: '#374151',
                    padding: '12px',
                    borderRadius: '8px',
                    border: 'none',
                    cursor: 'pointer',
                    fontSize: '14px'
                  }}
                >
                  Cancel
                </button>
              </div>
              
              {/* Delete button - only show when editing existing dog */}
              {editingDog && (
                <button
                  onClick={() => {
                    if (window.confirm(`Are you sure you want to delete ${editingDog.name}? This will also delete all training entries for this dog.`)) {
                      handleDeleteDog(editingDog.id);
                      setShowDogForm(false);
                      setEditingDog(null);
                      setDogFormData({ name: '', breed: '', age: '', spayedNeutered: 'unknown', photo: null });
                    }
                  }}
                  style={{
                    width: '100%',
                    background: '#fee2e2',
                    color: '#dc2626',
                    padding: '12px',
                    borderRadius: '8px',
                    border: '1px solid #fecaca',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '500',
                    marginTop: '10px'
                  }}
                >
                  🗑️ Delete Dog
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Add Entry Modal */}
      {showEntryForm && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '20px',
          zIndex: 1000
        }}>
          {/* Toast notifications for entry form */}
          {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}

          <div style={{
            background: 'white',
            borderRadius: '12px',
            padding: '30px',
            maxWidth: '600px',
            width: '100%',
            maxHeight: '90vh',
            overflowY: 'auto'
          }}>
            <h3 style={{ fontSize: '20px', fontWeight: 'bold', marginBottom: '20px' }}>
              {editingEntry ? 'Edit Training Entry' : 'New Training Entry'}
            </h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                <input
                  type="date"
                  value={entryData.date}
                  onChange={(e) => setEntryData({...entryData, date: e.target.value})}
                  style={{
                    padding: '12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '8px',
                    fontSize: '14px'
                  }}
                />
                <input
                  type="time"
                  value={entryData.time}
                  onChange={(e) => setEntryData({...entryData, time: e.target.value})}
                  style={{
                    padding: '12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '8px',
                    fontSize: '14px'
                  }}
                />
              </div>
              <select
                value={entryData.activity}
                onChange={(e) => setEntryData({...entryData, activity: e.target.value})}
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '8px',
                  fontSize: '14px'
                }}
              >
                <option value="">Select Activity</option>
                {activityOptions.map((activity) => (
                  <option key={activity} value={activity}>{activity}</option>
                ))}
              </select>
              {entryData.activity === 'Other' && (
                <input
                  type="text"
                  placeholder="Specify activity"
                  value={entryData.customActivity}
                  onChange={(e) => setEntryData({...entryData, customActivity: e.target.value})}
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '8px',
                    fontSize: '14px'
                  }}
                />
              )}
              <input
                type="number"
                placeholder="Duration (minutes)"
                value={entryData.duration}
                onChange={(e) => setEntryData({...entryData, duration: e.target.value})}
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '8px',
                  fontSize: '14px'
                }}
              />
              <div>
                <label style={{ fontSize: '14px', fontWeight: '500', marginBottom: '5px', display: 'block' }}>
                  Dog's Mood
                </label>
                <select
                  value={entryData.dogMood}
                  onChange={(e) => setEntryData({...entryData, dogMood: e.target.value})}
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '8px',
                    fontSize: '14px'
                  }}
                >
                  <option value="happy">😊 Happy</option>
                  <option value="neutral">😐 Neutral</option>
                  <option value="anxious">😰 Anxious</option>
                  <option value="frustrated">😤 Frustrated</option>
                </select>
              </div>
              <div>
                <label style={{ fontSize: '14px', fontWeight: '500', marginBottom: '5px', display: 'block' }}>
                  Success Level
                </label>
                <select
                  value={entryData.success}
                  onChange={(e) => setEntryData({...entryData, success: e.target.value})}
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '8px',
                    fontSize: '14px'
                  }}
                >
                  <option value="excellent">⭐ Excellent</option>
                  <option value="moderate">👍 Moderate</option>
                  <option value="challenging">😓 Challenging</option>
                </select>
              </div>
              <textarea
                placeholder="Training observations and notes..."
                value={entryData.observations}
                onChange={(e) => setEntryData({...entryData, observations: e.target.value})}
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '8px',
                  fontSize: '14px',
                  minHeight: '80px',
                  resize: 'vertical'
                }}
              />
              <textarea
                placeholder="Any issues or challenges? (optional)"
                value={entryData.issues}
                onChange={(e) => setEntryData({...entryData, issues: e.target.value})}
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '8px',
                  fontSize: '14px',
                  minHeight: '60px',
                  resize: 'vertical'
                }}
              />
              <input
                type="url"
                placeholder="Video link (optional)"
                value={entryData.videoLink}
                onChange={(e) => setEntryData({...entryData, videoLink: e.target.value})}
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '8px',
                  fontSize: '14px'
                }}
              />
              <input
                type="file"
                accept="image/*"
                multiple
                onChange={handleEntryPhotoUpload}
                style={{ fontSize: '14px' }}
              />
              {entryData.photos.length > 0 && (
                <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                  {entryData.photos.map((photo, idx) => (
                    <div key={idx} style={{ position: 'relative' }}>
                      <img
                        src={photo}
                        alt={`Preview ${idx + 1}`}
                        style={{
                          width: '80px',
                          height: '80px',
                          objectFit: 'cover',
                          borderRadius: '8px',
                          border: '2px solid #e5e7eb'
                        }}
                      />
                      <button
                        onClick={() => {
                          const newPhotos = [...entryData.photos];
                          newPhotos.splice(idx, 1);
                          setEntryData({...entryData, photos: newPhotos});
                        }}
                        style={{
                          position: 'absolute',
                          top: '-5px',
                          right: '-5px',
                          background: '#ef4444',
                          color: 'white',
                          border: 'none',
                          borderRadius: '50%',
                          width: '20px',
                          height: '20px',
                          cursor: 'pointer',
                          fontSize: '12px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              )}
              <div style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
                <button
                  onClick={editingEntry ? handleUpdateEntry : handleAddEntry}
                  style={{
                    flex: 1,
                    background: '#d97706',
                    color: 'white',
                    padding: '12px',
                    borderRadius: '8px',
                    border: 'none',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '500'
                  }}
                >
                  {editingEntry ? 'Update Entry' : 'Save Entry'}
                </button>
                <button
                  onClick={() => {
                    setShowEntryForm(false);
                    setEditingEntry(null);
                    setEntryData({
                      date: new Date().toISOString().split('T')[0],
                      time: new Date().toTimeString().slice(0, 5),
                      activity: '',
                      customActivity: '',
                      duration: '',
                      issues: '',
                      observations: '',
                      dogMood: 'neutral',
                      success: 'moderate',
                      photos: [],
                      videoLink: ''
                    });
                  }}
                  style={{
                    flex: 1,
                    background: '#f3f4f6',
                    color: '#374151',
                    padding: '12px',
                    borderRadius: '8px',
                    border: 'none',
                    cursor: 'pointer',
                    fontSize: '14px'
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Ask Question Modal */}
      {showQuestionForm && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '20px',
          zIndex: 1000
        }}>
          <div style={{
            background: 'white',
            borderRadius: '12px',
            padding: '30px',
            maxWidth: '500px',
            width: '100%'
          }}>
            <h3 style={{ fontSize: '20px', fontWeight: 'bold', marginBottom: '20px' }}>
              Ask Your Trainer
            </h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
              <select
                value={questionData.dogId}
                onChange={(e) => setQuestionData({...questionData, dogId: e.target.value})}
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '8px',
                  fontSize: '14px'
                }}
              >
                <option value="">Select a dog</option>
                {dogs.map((dog) => (
                  <option key={dog.id} value={dog.id}>{dog.name}</option>
                ))}
              </select>
              <textarea
                placeholder="What's your question?"
                value={questionData.question}
                onChange={(e) => setQuestionData({...questionData, question: e.target.value})}
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '8px',
                  fontSize: '14px',
                  minHeight: '120px',
                  resize: 'vertical'
                }}
              />
              <div style={{ display: 'flex', gap: '10px' }}>
                <button
                  onClick={handleSubmitQuestion}
                  style={{
                    flex: 1,
                    background: '#d97706',
                    color: 'white',
                    padding: '12px',
                    borderRadius: '8px',
                    border: 'none',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '500'
                  }}
                >
                  Submit Question
                </button>
                <button
                  onClick={() => {
                    setShowQuestionForm(false);
                    setQuestionData({ question: '', dogId: '' });
                  }}
                  style={{
                    flex: 1,
                    background: '#f3f4f6',
                    color: '#374151',
                    padding: '12px',
                    borderRadius: '8px',
                    border: 'none',
                    cursor: 'pointer',
                    fontSize: '14px'
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add/Edit Notes Modal */}
      {showWeeklySummary && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '20px',
          zIndex: 1000,
          overflowY: 'auto'
        }}>
          <div style={{
            background: 'white',
            borderRadius: '12px',
            padding: '30px',
            maxWidth: '600px',
            width: '100%'
          }}>
            <h3 style={{ fontSize: '20px', fontWeight: 'bold', marginBottom: '10px' }}>
              {weeklyData.summary ? 'Edit Your Notes' : 'Add Your Notes'}
            </h3>
            <p style={{ fontSize: '14px', color: '#6b7280', marginBottom: '20px' }}>
              Week: {new Date(weeklyData.weekStart).toLocaleDateString()} - {new Date(new Date(weeklyData.weekStart).getTime() + 6 * 24 * 60 * 60 * 1000).toLocaleDateString()}
            </p>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
              <div>
                <label style={{ fontSize: '14px', fontWeight: '500', marginBottom: '8px', display: 'block' }}>
                  Your Reflection & Notes:
                </label>
                <p style={{ fontSize: '12px', color: '#6b7280', marginBottom: '8px', fontStyle: 'italic' }}>
                  The training stats are auto-generated from your entries. Use this space to add your personal insights, observations, challenges faced, or goals for next week!
                </p>
                <textarea
                  placeholder="What went well this week? What challenges did you face? What are your goals for next week? Any questions or concerns for your trainer?"
                  value={weeklyData.summary}
                  onChange={(e) => setWeeklyData({...weeklyData, summary: e.target.value})}
                  autoFocus
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '2px solid #d97706',
                    borderRadius: '8px',
                    fontSize: '14px',
                    minHeight: '150px',
                    resize: 'vertical',
                    fontFamily: 'inherit'
                  }}
                />
              </div>

              <div style={{ display: 'flex', gap: '10px' }}>
                <button
                  onClick={() => {
                    // Calculate stats for this week
                    const weekStart = new Date(weeklyData.weekStart);
                    const weekEnd = new Date(weekStart);
                    weekEnd.setDate(weekEnd.getDate() + 7);
                    
                    const weekEntries = entries.filter(entry => {
                      const entryDate = new Date(entry.timestamp);
                      return entryDate >= weekStart && entryDate < weekEnd;
                    });

                    const stats = {
                      totalSessions: weekEntries.length,
                      totalMinutes: weekEntries.reduce((sum, e) => sum + (parseInt(e.duration) || 0), 0),
                      activities: {},
                      moods: { happy: 0, neutral: 0, frustrated: 0 },
                      success: { high: 0, moderate: 0, low: 0 },
                      issues: []
                    };

                    weekEntries.forEach(e => {
                      stats.activities[e.activity] = (stats.activities[e.activity] || 0) + 1;
                      if (e.dogMood) stats.moods[e.dogMood]++;
                      if (e.success) stats.success[e.success]++;
                      if (e.issues && e.issues.trim()) stats.issues.push(e.issues);
                    });

                    const summaryData = {
                      weekStart: weeklyData.weekStart,
                      summary: weeklyData.summary,
                      stats,
                      createdAt: new Date().toISOString()
                    };

                    // Update or create
                    const savePromise = weeklyData.existingId
                      ? updateDoc(doc(db, `users/${currentUser.uid}/summaries`, weeklyData.existingId), summaryData)
                      : setDoc(doc(collection(db, `users/${currentUser.uid}/summaries`)), summaryData);

                    savePromise
                      .then(() => {
                        loadWeeklySummaries(currentUser.uid);
                        setWeeklyData({ summary: '', weekStart: new Date().toISOString().split('T')[0] });
                        setShowWeeklySummary(false);
                        showToast(weeklyData.existingId ? 'Notes updated!' : 'Notes saved!', 'success');
                      })
                      .catch(error => {
                        showToast('Error saving notes: ' + error.message, 'error');
                      });
                  }}
                  style={{
                    flex: 1,
                    background: '#d97706',
                    color: 'white',
                    padding: '12px',
                    borderRadius: '8px',
                    border: 'none',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '500'
                  }}
                >
                  {weeklyData.existingId ? 'Update Notes' : 'Save Notes'}
                </button>
                <button
                  onClick={() => {
                    setShowWeeklySummary(false);
                    setWeeklyData({ summary: '', weekStart: new Date().toISOString().split('T')[0] });
                  }}
                  style={{
                    flex: 1,
                    background: '#f3f4f6',
                    color: '#374151',
                    padding: '12px',
                    borderRadius: '8px',
                    border: 'none',
                    cursor: 'pointer',
                    fontSize: '14px'
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Tips Modal */}
      {showTipsModal && (
        <TipsModal onClose={() => setShowTipsModal(false)} />
      )}
      
      {/* Settings Modal */}
      {showSettings && (
        <UserSettings
          currentUser={currentUser}
          db={db}
          onClose={() => setShowSettings(false)}
          showToast={showToast}
          onDeleteAccount={handleDeleteAccount}
        />
      )}

      <Footer />
    </div>
  );
}
function Footer() {
  return (
    <div style={{
      background: 'white',
      borderTop: '2px solid #fcd34d',
      padding: '30px 20px 5px 20px',
      marginTop: '40px'
    }}>
      <div style={{
        maxWidth: '1200px',
        margin: '0 auto',
        textAlign: 'center'
      }}>
        <img 
          src="https://methoddogtraining.com/wp-content/uploads/2023/02/cropped-Color-logo-no-background-1-1.png" 
          alt="Method Dog Training" 
          style={{ maxWidth: '300px', width: '100%', height: 'auto', marginBottom: '1px' }}
        />
        
        {/* Links - Stack on mobile */}
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '12px',
          fontSize: '14px',
          color: '#d97706',
          fontWeight: '500'
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: '8px 15px'
            }}>
            <a 
              href="https://methoddogtraining.com/contact" 
              target="_blank" 
              rel="noopener noreferrer"
              style={{ 
                color: '#d97706', 
                textDecoration: 'none',
                whiteSpace: 'nowrap'
              }}
            >
              Book A Session
            </a>
            <span style={{ color: '#d1d5db' }}>•</span>
            <a 
              href="https://methoddogtraining.com" 
              target="_blank" 
              rel="noopener noreferrer"
              style={{ 
                color: '#d97706', 
                textDecoration: 'none',
                whiteSpace: 'nowrap'
              }}
            >
              MethodDogTraining.com
            </a>
          </div>
          
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: '15px',
            flexWrap: 'wrap'
          }}>
            <a 
              href="tel:512-765-4505"
              style={{ 
                color: '#d97706', 
                textDecoration: 'none',
                whiteSpace: 'nowrap',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '5px'
              }}
            >
              Call <span>512-765-4505</span>
            </a>
            <span style={{ color: '#d1d5db' }}> or </span>
            <a 
              href="sms:512-765-4505"
              style={{ 
                color: '#d97706', 
                textDecoration: 'none',
                whiteSpace: 'nowrap',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '5px'
              }}
            >
              <span>Text Us</span>
            </a>
          </div>
        </div>
        
        {/* Copyright/Additional Info */}
        <p style={{
          marginTop: '15px',
          fontSize: '11px',
          color: '#9ca3af'
        }}>
          © {new Date().getFullYear()} Method Dog Training. All rights reserved.
        </p>
      </div>
    </div>
  );
}
function Toast({ message, type, onClose }) {
  return (
    <div style={{
      position: 'fixed',
      top: '20px',
      right: '20px',
      background: type === 'success' ? '#10b981' : type === 'error' ? '#ef4444' : '#f59e0b',
      color: 'white',
      padding: '16px 24px',
      borderRadius: '8px',
      boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.3)',
      zIndex: 9999,
      minWidth: '300px',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      animation: 'slideIn 0.3s ease-out'
    }}>
      <span style={{ fontSize: '15px', fontWeight: '500' }}>{message}</span>
      <button
        onClick={onClose}
        style={{
          background: 'transparent',
          border: 'none',
          color: 'white',
          fontSize: '20px',
          cursor: 'pointer',
          marginLeft: '15px',
          padding: '0',
          lineHeight: '1'
        }}
      >
        ×
      </button>
    </div>
  );
}

export default App;
